import { create } from 'zustand';

/**
 * Authentication state (§17.3 Zustand, §21.4).
 *
 * The access token lives ONLY here, in memory. It is never written to
 * localStorage or sessionStorage — §21.4 prohibits it, and the practical reason
 * is that anything readable by JavaScript is exfiltrable by an XSS foothold.
 *
 * Durability comes from the httpOnly refresh cookie instead: on a reload this
 * store is empty, and the app silently calls /auth/refresh, which the browser
 * accompanies with a cookie no script can read.
 */

export interface Session {
  readonly accessToken: string;
  readonly userId: string;
  readonly organisationId: string;
  /** Estate-wide role (§6). 'admin' unlocks the platform overview; default 'none'. */
  readonly platformRole: 'none' | 'admin';
}

interface AuthState {
  session: Session | null;
  /** True until the initial silent refresh has settled. */
  bootstrapping: boolean;
  activeWorkspaceId: string | null;
  setSession: (session: Session | null) => void;
  setBootstrapped: () => void;
  setActiveWorkspace: (workspaceId: string | null) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  bootstrapping: true,
  activeWorkspaceId: null,
  setSession: (session) => set({ session }),
  setBootstrapped: () => set({ bootstrapping: false }),
  setActiveWorkspace: (activeWorkspaceId) => set({ activeWorkspaceId }),
  clear: () => set({ session: null, activeWorkspaceId: null }),
}));

/**
 * Read the token outside React (the API client needs it per request without
 * subscribing to re-renders).
 */
export const currentAccessToken = (): string | null =>
  useAuthStore.getState().session?.accessToken ?? null;
